// Purpose: drill-down detail Modal for an audit row. Opens when an
// admin clicks a sequenceNumber in admin/Audit.tsx. Renders the full
// row + payload (prettified JSON), hash-chain context, denormalized +
// current actor info (with drift indicator), type-dispatched target
// detail, and "related rows" (same actor, +/- 5 minutes from this row's
// occurredAt). Sequence numbers in the related list are themselves
// clickable so an investigator can chain drill-downs.

import { useEffect, useState } from 'react';
import {
  fetchAuditDetail,
  type AuditDetailResult,
  type AuditTargetDetail,
} from '../../api/admin-audit.ts';
import { actionLabel } from '../../lib/audit-verbs.ts';
import { formatDateTime } from '../../lib/format.ts';
import { Badge, Button, Modal } from '../../components/ui/index.ts';

interface DenormalizedActor {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

function readActor(payload: unknown): DenormalizedActor | null {
  if (!payload || typeof payload !== 'object') return null;
  const inner = (payload as { payload?: unknown }).payload;
  const candidate =
    inner && typeof inner === 'object'
      ? (inner as { _actor?: unknown })._actor
      : (payload as { _actor?: unknown })._actor;
  if (!candidate || typeof candidate !== 'object') return null;
  const a = candidate as Partial<DenormalizedActor>;
  if (!a.id || !a.email) return null;
  return {
    id: a.id,
    email: a.email,
    displayName: a.displayName ?? a.email,
    role: a.role ?? '',
  };
}

function payloadInner(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  const inner = (payload as { payload?: unknown }).payload;
  return inner && typeof inner === 'object' ? (inner as Record<string, unknown>) : {};
}

function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export interface AuditDetailModalProps {
  rowId: string | null;
  /** Called when the modal closes (Escape, backdrop, X). */
  onClose: () => void;
  /** Drill into a different row from the related-list. */
  onNavigate: (id: string) => void;
}

export function AuditDetailModal({ rowId, onClose, onNavigate }: AuditDetailModalProps) {
  const [detail, setDetail] = useState<AuditDetailResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!rowId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAuditDetail(rowId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rowId]);

  const onCopyJson = (): void => {
    if (!detail) return;
    void navigator.clipboard.writeText(prettyJson(detail));
  };

  return (
    <Modal
      open={rowId !== null}
      onClose={onClose}
      title={detail ? `Audit row #${detail.row.sequenceNumber}` : 'Audit row'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onCopyJson} disabled={!detail}>
            Copy as JSON
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="text-body-s text-fg-muted">Loading…</div>
      ) : error ? (
        <div className="text-body-s text-status-danger">{error}</div>
      ) : detail ? (
        <DetailBody detail={detail} onNavigate={onNavigate} />
      ) : null}
    </Modal>
  );
}

function DetailBody({
  detail,
  onNavigate,
}: {
  detail: AuditDetailResult;
  onNavigate: (id: string) => void;
}) {
  const { row, prev, next, target, related } = detail;
  const actor = readActor(row.payload);
  const inner = payloadInner(row.payload);

  return (
    <div className="flex flex-col gap-5">
      {/* Header line: action + when + chain status */}
      <section className="flex flex-wrap items-center gap-3">
        <span className="text-body-m text-fg-primary font-medium">{actionLabel(row.action)}</span>
        <span className="text-body-s text-fg-muted">{formatDateTime(row.occurredAt)}</span>
        {row.chainOk ? (
          <Badge tone="success">Chain OK</Badge>
        ) : (
          <Badge tone="danger">Chain BROKEN</Badge>
        )}
        <Badge tone="neutral">{row.actorType}</Badge>
      </section>

      {/* Actor */}
      <Section title="Actor">
        {actor ? (
          <div className="text-body-s text-fg-primary">
            <span className="font-medium">{actor.displayName}</span>{' '}
            <span className="text-fg-muted">&lt;{actor.email}&gt;</span>
            {actor.role ? (
              <Badge tone="neutral" className="ml-2">
                {actor.role}
              </Badge>
            ) : null}
            <div className="text-caption text-fg-muted mt-1 font-mono">id: {actor.id}</div>
            <p className="text-caption text-fg-muted mt-2">
              Snapshot from row payload (immutable). The user&apos;s current name / email / role may
              have drifted — drill into <code>auth.user.role_changed</code> for that row&apos;s
              actor to see the history.
            </p>
          </div>
        ) : row.actorType === 'system' ? (
          <span className="text-body-s text-fg-muted italic">system</span>
        ) : row.actorType === 'external' ? (
          <div className="text-body-s text-fg-muted">
            <Badge tone="neutral" className="mr-2">
              external recipient
            </Badge>
            <span className="font-mono">{row.actorId ?? '(no actor)'}</span>
            <p className="text-caption text-fg-muted mt-1">
              Anonymous external action — no actor identity captured by design. The token in the URL
              is the capability.
            </p>
          </div>
        ) : (
          <div className="text-body-s text-fg-muted">
            <span className="font-mono">{row.actorId ?? '(no actor)'}</span>
            <Badge tone="warning" className="ml-2">
              legacy
            </Badge>
            <p className="text-caption text-fg-muted mt-1">
              Pre-8b.11 row — actor identity wasn&apos;t denormalized into the payload.
            </p>
          </div>
        )}
      </Section>

      {/* Target */}
      <Section title="Target">
        {target ? (
          <TargetView target={target} />
        ) : (
          <span className="text-body-s text-fg-muted">
            {row.targetType ? `${row.targetType}:${row.targetId ?? '—'}` : 'No target'}
          </span>
        )}
      </Section>

      {/* Hash chain context */}
      <Section title="Hash chain">
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-body-s">
          <dt className="text-fg-muted">Sequence</dt>
          <dd className="font-mono text-fg-primary">#{row.sequenceNumber}</dd>
          <dt className="text-fg-muted">Prev row</dt>
          <dd>
            {prev ? (
              <button
                type="button"
                className="font-mono text-accent-primary hover:underline"
                onClick={() => onNavigate(prev.id)}
              >
                #{prev.sequenceNumber} — {actionLabel(prev.action)}
              </button>
            ) : (
              <span className="text-fg-muted">— (genesis)</span>
            )}
          </dd>
          <dt className="text-fg-muted">Next row</dt>
          <dd>
            {next ? (
              <button
                type="button"
                className="font-mono text-accent-primary hover:underline"
                onClick={() => onNavigate(next.id)}
              >
                #{next.sequenceNumber} — {actionLabel(next.action)}
              </button>
            ) : (
              <span className="text-fg-muted">— (chain tail)</span>
            )}
          </dd>
        </dl>
      </Section>

      {/* Related rows */}
      <Section title={`Related rows (same actor ±5 min, ${related.length})`}>
        {related.length === 0 ? (
          <span className="text-body-s text-fg-muted">No related rows.</span>
        ) : (
          <ul className="flex flex-col gap-1">
            {related.map((r) => (
              <li key={r.id} className="text-body-s">
                <button
                  type="button"
                  className="text-accent-primary hover:underline font-mono"
                  onClick={() => onNavigate(r.id)}
                >
                  #{r.sequenceNumber}
                </button>{' '}
                <span className="text-fg-primary">{actionLabel(r.action)}</span>
                <span className="text-fg-muted ml-2">{formatDateTime(r.occurredAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Inner payload */}
      <Section title="Payload">
        <pre className="text-caption font-mono p-3 rounded-md bg-bg-tertiary border border-border-subtle overflow-x-auto whitespace-pre">
          {prettyJson(inner)}
        </pre>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-label uppercase text-fg-secondary mb-2">{title}</h3>
      {children}
    </section>
  );
}

function TargetView({ target }: { target: AuditTargetDetail }) {
  switch (target.kind) {
    case 'share':
      return (
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-body-s">
          <dt className="text-fg-muted">Kind</dt>
          <dd>
            <Badge tone="info">share</Badge>
          </dd>
          <dt className="text-fg-muted">Name</dt>
          <dd className="text-fg-primary">{target.name}</dd>
          <dt className="text-fg-muted">Status</dt>
          <dd>
            <Badge tone="neutral">{target.status}</Badge>
          </dd>
          <dt className="text-fg-muted">Recipient</dt>
          <dd className="text-fg-primary">{target.recipientEmail ?? '—'}</dd>
          <dt className="text-fg-muted">Created</dt>
          <dd className="text-fg-muted">{formatDateTime(target.createdAt)}</dd>
          <dt className="text-fg-muted">Expires</dt>
          <dd className="text-fg-muted">
            {target.expiresAt ? formatDateTime(target.expiresAt) : '—'}
          </dd>
          <dt className="text-fg-muted">id</dt>
          <dd className="font-mono text-caption text-fg-muted">{target.id}</dd>
        </dl>
      );
    case 'dropbox':
      return (
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-body-s">
          <dt className="text-fg-muted">Kind</dt>
          <dd>
            <Badge tone="info">dropbox</Badge>
          </dd>
          <dt className="text-fg-muted">Label</dt>
          <dd className="text-fg-primary">{target.label}</dd>
          <dt className="text-fg-muted">Status</dt>
          <dd>
            <Badge tone="neutral">{target.status}</Badge>
          </dd>
          <dt className="text-fg-muted">Created</dt>
          <dd className="text-fg-muted">{formatDateTime(target.createdAt)}</dd>
          <dt className="text-fg-muted">Expires</dt>
          <dd className="text-fg-muted">
            {target.expiresAt ? formatDateTime(target.expiresAt) : '—'}
          </dd>
          <dt className="text-fg-muted">id</dt>
          <dd className="font-mono text-caption text-fg-muted">{target.id}</dd>
        </dl>
      );
    case 'user':
      return (
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-body-s">
          <dt className="text-fg-muted">Kind</dt>
          <dd>
            <Badge tone="info">user</Badge>
          </dd>
          <dt className="text-fg-muted">Identity</dt>
          <dd className="text-fg-primary">
            {target.displayName} &lt;{target.email}&gt;
          </dd>
          <dt className="text-fg-muted">Role</dt>
          <dd>
            <Badge tone="neutral">{target.role}</Badge>
          </dd>
          <dt className="text-fg-muted">Active</dt>
          <dd>{target.active ? 'yes' : 'no'}</dd>
          <dt className="text-fg-muted">id</dt>
          <dd className="font-mono text-caption text-fg-muted">{target.id}</dd>
        </dl>
      );
    case 'upload':
      return (
        <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-body-s">
          <dt className="text-fg-muted">Kind</dt>
          <dd>
            <Badge tone="info">upload</Badge>
          </dd>
          <dt className="text-fg-muted">Filename</dt>
          <dd className="text-fg-primary">{target.filename}</dd>
          <dt className="text-fg-muted">Size</dt>
          <dd className="text-fg-muted">{target.sizeBytes} bytes</dd>
          <dt className="text-fg-muted">MIME</dt>
          <dd className="text-fg-muted">{target.mimeType}</dd>
          <dt className="text-fg-muted">Status</dt>
          <dd>
            <Badge tone="neutral">{target.status}</Badge>
          </dd>
          <dt className="text-fg-muted">id</dt>
          <dd className="font-mono text-caption text-fg-muted">{target.id}</dd>
        </dl>
      );
    case 'unknown':
      return (
        <span className="text-body-s text-fg-muted font-mono">
          {target.targetType}:{target.targetId} <Badge tone="warning">not resolved</Badge>
        </span>
      );
  }
}
